import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Zap, Loader2, ShieldAlert } from "lucide-react";

const Toggle = ({ label, checked, onChange, testid, disabled }) => (
  <label className={`flex items-center justify-between gap-3 py-2 ${disabled ? "opacity-50" : "cursor-pointer"}`}>
    <span className="text-sm text-slate-600">{label}</span>
    <button type="button" disabled={disabled} data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative w-10 h-6 rounded-full transition-colors ${checked ? "bg-cobalt" : "bg-slate-200"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4.5 left-0.5 translate-x-[18px]" : "left-0.5"}`} />
    </button>
  </label>
);

const Num = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs font-medium text-slate-500">{label}</label>
    <input type="number" min="0" value={value ?? ""} data-testid={testid}
      onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30" />
  </div>
);

export default function AutoAcceptPrefs({ onClose }) {
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statesText, setStatesText] = useState("");

  useEffect(() => {
    api.get("/routing/auto-accept-prefs").then(({ data }) => {
      setData(data); setPrefs(data.prefs); setStatesText((data.prefs.states || []).join(", "));
    }).catch(() => toast.error("Could not load preferences"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const states = statesText.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
      const { data: res } = await api.put("/routing/auto-accept-prefs", { ...prefs, states });
      setPrefs(res.prefs);
      toast.success("Auto-Accept preferences saved");
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save preferences");
    } finally { setSaving(false); }
  };

  if (!prefs) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));
  const canEnable = data?.membership_active && data?.payment_method_attached;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="auto-accept-modal">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-5 h-5 text-amber-500" />
          <h2 className="font-heading text-lg font-bold text-navy">Rear-End Priority Auto-Accept</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">Get first-priority reservations on verified rear-end cases with other-driver insurance. Disabled by default. Your stored card is only charged the disclosed per-case platform fee when you accept a case.</p>

        {!canEnable && (
          <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-xs text-amber-700" data-testid="auto-accept-requirements-warning">
            <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
            <span>To enable Auto-Accept you need {!data?.membership_active && "an active subscription"}{!data?.membership_active && !data?.payment_method_attached && " and "}{!data?.payment_method_attached && "a stored payment method"}. Manage these in Billing.</span>
          </div>
        )}

        <Toggle label="Auto-Accept enabled" checked={prefs.enabled} onChange={(v) => set("enabled", v)} testid="aa-enabled" disabled={!canEnable && !prefs.enabled} />
        <Toggle label="Pause Auto-Accept" checked={prefs.paused} onChange={(v) => set("paused", v)} testid="aa-paused" />
        <Toggle label="RN completed required" checked={prefs.rn_completed_required} onChange={(v) => set("rn_completed_required", v)} testid="aa-rn-required" />
        <Toggle label="Drone requested required" checked={prefs.drone_requested_required} onChange={(v) => set("drone_requested_required", v)} testid="aa-drone-requested" />
        <Toggle label="Drone completed required" checked={prefs.drone_completed_required} onChange={(v) => set("drone_completed_required", v)} testid="aa-drone-completed" />
        <Toggle label="Include commercial vehicles" checked={prefs.include_commercial} onChange={(v) => set("include_commercial", v)} testid="aa-commercial" />
        <Toggle label="24/7 availability (off = business hours 8am–6pm)" checked={prefs.hours === "24_7"} onChange={(v) => set("hours", v ? "24_7" : "business")} testid="aa-hours" />

        <div className="grid grid-cols-2 gap-3 mt-3">
          <Num label="Minimum Trust Score" value={prefs.min_trust_score} onChange={(v) => set("min_trust_score", v)} testid="aa-min-trust" />
          <Num label="Max spend / month ($)" value={prefs.max_spend_month} onChange={(v) => set("max_spend_month", v)} testid="aa-max-spend" />
          <Num label="Max cases / day" value={prefs.max_per_day} onChange={(v) => set("max_per_day", v)} testid="aa-max-day" />
          <Num label="Max cases / week" value={prefs.max_per_week} onChange={(v) => set("max_per_week", v)} testid="aa-max-week" />
          <Num label="Max cases / month" value={prefs.max_per_month} onChange={(v) => set("max_per_month", v)} testid="aa-max-month" />
          <div>
            <label className="text-xs font-medium text-slate-500">States (comma separated, empty = all)</label>
            <input value={statesText} data-testid="aa-states" onChange={(e) => setStatesText(e.target.value)} placeholder="TX, FL"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30" />
          </div>
        </div>

        <div className="mt-4 rounded-xl bg-slate-50 p-3 space-y-2">
          <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={prefs.terms_accepted} onChange={(e) => set("terms_accepted", e.target.checked)} data-testid="aa-terms" className="mt-0.5" />
            I accept the Auto-Accept terms, including reservation windows, refund and dispute rules.
          </label>
          <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={prefs.charges_authorized} onChange={(e) => set("charges_authorized", e.target.checked)} data-testid="aa-charges" className="mt-0.5" />
            I authorize per-case platform charges (including any RN or drone platform fees) to my stored payment method, within my configured spending limits. Charges are never a percentage of legal fees or settlement value.
          </label>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-medium">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="aa-save" className="px-5 py-2.5 rounded-full bg-navy text-white text-sm font-medium hover:opacity-90 disabled:opacity-50">{saving ? "Saving…" : "Save preferences"}</button>
        </div>
      </div>
    </div>
  );
}
